/**
 * Bero platform console — Societies. GET /api/platform/societies?q= lists
 * every LIVE society with headline counts; search filters by name/city
 * server-side, debounced 300ms so typing doesn't spam the API. Row press →
 * the society profile, where SUPPORT/SUPER_ADMIN can enter it to operate the
 * ordinary society-admin console on its behalf (Task 6/7 hand-off).
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View, useWindowDimensions } from 'react-native';

import type { PillStatus } from '@bero/tokens';
import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { timeAgo, useFetch } from '@/features/community/lib';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Hairline, Input, Pill, Screen, Skeleton } from '@/ui';

type SocietyRow = {
  id: number; name: string; city: string; plan: string;
  flats: number; residents: number; admins: number; lastActivity: string | null;
};

async function fetchSocieties(q: string): Promise<SocietyRow[]> {
  const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
  const body = await api.get<{ items: SocietyRow[] }>(`/api/platform/societies${qs}`);
  return Array.isArray(body.items) ? body.items : [];
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

// Plan tiers aren't in the Pill component's fixed status vocabulary — borrow
// the closest existing color tier (grey/blue/green) and supply our own label.
const PLAN_TONE: Record<string, PillStatus> = { trial: 'pending', starter: 'open', pro: 'approved' };
const PLAN_LABEL: Record<string, string> = { trial: 'Trial', starter: 'Starter', pro: 'Pro' };

// `count` fits the longest header word ("RESIDENTS" in the label style) on one
// line — narrower and the header wraps mid-word above the numbers.
const COLW = { plan: 84, count: 78, activity: 100 };

function HeaderRow() {
  const { t } = useT();
  const cols: [DictKey, number, boolean][] = [
    ['plat_soc_col_plan', COLW.plan, false],
    ['plat_soc_col_flats', COLW.count, true],
    ['plat_soc_col_residents', COLW.count, true],
    ['plat_soc_col_admins', COLW.count, true],
    ['plat_soc_col_activity', COLW.activity, true],
  ];
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingHorizontal: space.s2, paddingBottom: space.s2 }}>
      <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>{t('plat_soc_col_society')}</AppText>
      {cols.map(([key, w, right]) => (
        <AppText key={key} numberOfLines={1} variant="label" tone="tertiary" style={{ width: w, textAlign: right ? 'right' : 'left' }}>
          {t(key)}
        </AppText>
      ))}
    </View>
  );
}

function SocietyRowItem({ row, wide }: { row: SocietyRow; wide: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  const activity = row.lastActivity ? timeAgo(new Date(row.lastActivity)) : '—';
  const plan = row.plan.toLowerCase();

  // Below the table breakpoint the fixed columns squeeze the name column to
  // nothing and truncate the headers, so the row stacks instead: identity on
  // top, the same numbers as one labelled line underneath.
  if (!wide) {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={() => router.push(`/platform/society/${row.id}`)}
        style={({ pressed }) => ({
          gap: 6, paddingVertical: 14, paddingHorizontal: space.s2,
          backgroundColor: pressed ? colors.canvas1 : 'transparent',
        })}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <AppText variant="bodyMd" weight="500">{row.name}</AppText>
            <AppText variant="bodySm" tone="tertiary">{row.city}</AppText>
          </View>
          <Pill status={PLAN_TONE[plan] ?? 'pending'} label={PLAN_LABEL[plan] ?? row.plan} />
        </View>
        <AppText variant="bodySm" tone="secondary">
          {[
            `${row.flats} ${t(row.flats === 1 ? 'plat_soc_one_flat' : 'plat_soc_col_flats').toLowerCase()}`,
            `${row.residents} ${t(row.residents === 1 ? 'plat_soc_one_resident' : 'plat_soc_col_residents').toLowerCase()}`,
            `${row.admins} ${t(row.admins === 1 ? 'plat_soc_one_admin' : 'plat_soc_col_admins').toLowerCase()}`,
            activity,
          ].join(' · ')}
        </AppText>
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/platform/society/${row.id}`)}
      style={({ pressed }) => ({
        flexDirection: 'row', alignItems: 'center', gap: space.s3,
        paddingVertical: 14, paddingHorizontal: space.s2,
        backgroundColor: pressed ? colors.canvas1 : 'transparent',
      })}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <AppText variant="bodyMd" weight="500" numberOfLines={1}>{row.name}</AppText>
        <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{row.city}</AppText>
      </View>
      <View style={{ width: COLW.plan }}>
        <Pill status={PLAN_TONE[plan] ?? 'pending'} label={PLAN_LABEL[plan] ?? row.plan} />
      </View>
      <AppText mono variant="bodySm" style={{ width: COLW.count, textAlign: 'right' }}>{row.flats}</AppText>
      <AppText mono variant="bodySm" style={{ width: COLW.count, textAlign: 'right' }}>{row.residents}</AppText>
      <AppText mono variant="bodySm" style={{ width: COLW.count, textAlign: 'right' }}>{row.admins}</AppText>
      <AppText variant="bodySm" tone="tertiary" numberOfLines={1} style={{ width: COLW.activity, textAlign: 'right' }}>
        {activity}
      </AppText>
    </Pressable>
  );
}

export default function PlatformSocietiesScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [q, setQ] = React.useState('');
  const debouncedQ = useDebounced(q, 300);
  const { data, loading, error, refetch } = useFetch(() => fetchSocieties(debouncedQ), [debouncedQ]);
  const rows = data ?? [];
  // Same breakpoint the console shell uses for sidebar-vs-scroller.
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= 1024;

  return (
    <Screen padded fullWidth style={{ paddingTop: space.s5 }}>
      <Input
        value={q}
        onChangeText={setQ}
        placeholder={t('plat_soc_search_ph')}
        style={{ marginBottom: space.s5, maxWidth: 360 }}
      />
      {loading ? (
        <View style={{ gap: space.s2 }}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={48} />)}
        </View>
      ) : error ? (
        <EmptyState
          icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
          title={t('plat_soc_err')}
          cta={t('retry')}
          onCta={() => void refetch()}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="building" size={40} color={colors.textTertiary} />}
          title={t('plat_soc_empty')}
        />
      ) : (
        <View>
          {wide ? <><HeaderRow /><Hairline /></> : null}
          {rows.map((s, i) => (
            <View key={s.id}>
              <SocietyRowItem row={s} wide={wide} />
              {i < rows.length - 1 ? <Hairline /> : null}
            </View>
          ))}
        </View>
      )}
    </Screen>
  );
}
